// src/pages/admin/AdminParents.tsx
import { useState } from "react";
import {
  useAdminGetParentsQuery,
  useAdminGetParentQuery,
  useAdminUpdateParentMutation,
} from "../../app/api/adminExtApi";
import type {
  AdminParent,
  AdminParentDetail,
  AdminParentChild,
  AdminParentEnrolment,
} from "../../app/api/adminExtApi";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  TableContainer,
  Th,
  Td,
  Pagination,
  SearchInput,
  Avatar,
  TrackChip,
  EnrolChip,
  Button,
  formatNaira,
  formatDate,
} from "./AdminShared";

import { Users } from "lucide-react";
/* ─── Parent drawer ─────────────────────────────────────────────── */
function ParentDrawer({
  parentId,
  onClose,
}: {
  parentId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useAdminGetParentQuery(parentId);
  const [updateParent, { isLoading: saving }] = useAdminUpdateParentMutation();
  const parent: AdminParentDetail | null = data?.data ?? null;

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [saveOk, setSaveOk] = useState("");

  const startEdit = () => {
    if (!parent) return;
    setFirstName(parent.firstName);
    setLastName(parent.lastName);
    setPhone(parent.phone ?? "");
    setSaveErr("");
    setSaveOk("");
    setEditing(true);
  };

  const handleSave = async () => {
    if (!parent) return;
    setSaveErr("");
    setSaveOk("");
    try {
      await updateParent({
        id: parent.id,
        body: { firstName, lastName, phone },
      }).unwrap();
      setSaveOk("Saved");
      setEditing(false);
    } catch (e: any) {
      setSaveErr(e?.data?.error ?? "Failed to save");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]";

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[520px]"
        role="dialog"
        aria-modal
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          {isLoading ? (
            <Skeleton className="h-8 w-48" />
          ) : (
            <div className="flex items-center gap-3">
              <Avatar
                firstName={parent?.firstName ?? "?"}
                lastName={parent?.lastName ?? ""}
                size="md"
              />
              <div>
                <h2 className="text-base font-bold text-[#16213e]">
                  {parent?.firstName} {parent?.lastName}
                </h2>
                <p className="text-xs text-[#8189a0]">{parent?.email}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            {!editing && !isLoading && (
              <Button variant="secondary" size="sm" onClick={startEdit}>
                Edit
              </Button>
            )}
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 rounded-xl" />
              ))}
            </div>
          ) : !parent ? (
            <p className="text-sm text-[#8189a0]">Failed to load parent.</p>
          ) : (
            <>
              {/* Edit form */}
              {editing ? (
                <div className="rounded-[18px] border border-[#cdd4e2] bg-[#f1f3f8] p-4 space-y-3">
                  <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                    Edit profile
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-[12.5px] font-semibold text-[#5a6278]">
                        First name
                      </label>
                      <input
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={inputCls + " mt-1"}
                      />
                    </div>
                    <div>
                      <label className="text-[12.5px] font-semibold text-[#5a6278]">
                        Last name
                      </label>
                      <input
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={inputCls + " mt-1"}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-[12.5px] font-semibold text-[#5a6278]">
                        Phone
                      </label>
                      <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+234…"
                        className={inputCls + " mt-1"}
                      />
                    </div>
                  </div>
                  {saveErr && (
                    <p className="text-xs text-[#e57373]">{saveErr}</p>
                  )}
                  {saveOk && (
                    <p className="text-xs font-semibold text-[#6cc2a0]">
                      {saveOk}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      className="flex-1"
                      onClick={() => setEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      className="flex-1"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? "Saving…" : "Save changes"}
                    </Button>
                  </div>
                </div>
              ) : (
                /* Profile summary */
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Phone", value: parent.phone ?? "—" },
                    { label: "Joined", value: formatDate(parent.createdAt) },
                    { label: "Children", value: String(parent.childCount) },
                    {
                      label: "Email verified",
                      value: parent.isEmailVerified ? "Yes" : "No",
                    },
                    {
                      label: "Active enrolments",
                      value: String(parent.activeEnrolments),
                    },
                    {
                      label: "Total paid",
                      value: formatNaira(parent.totalPaid),
                    },
                  ].map((row: { label: string; value: string }) => (
                    <div
                      key={row.label}
                      className="rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-3 py-2.5"
                    >
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#8189a0]">
                        {row.label}
                      </p>
                      <p className="mt-0.5 text-xs font-semibold text-[#16213e]">
                        {row.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Children */}
              {parent.children.length > 0 && (
                <div>
                  <p className="mb-3 text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                    Children ({parent.children.length})
                  </p>
                  <div className="space-y-3">
                    {parent.children.map((child: AdminParentChild) => (
                      <div
                        key={child.id}
                        className="rounded-[18px] border border-[#e5e8ef] bg-white p-4"
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2.5">
                            <Avatar
                              firstName={child.firstName}
                              lastName={child.lastName}
                              size="sm"
                            />
                            <div>
                              <p className="text-sm font-bold text-[#16213e]">
                                {child.firstName} {child.lastName}
                              </p>
                              <p className="text-[11.5px] text-[#8189a0]">
                                Age {child.age}
                              </p>
                            </div>
                          </div>
                          <TrackChip track={child.track} />
                        </div>
                        {/* Child enrolments */}
                        {child.enrolments.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            {child.enrolments.map(
                              (enr: AdminParentEnrolment) => (
                                <div
                                  key={enr.id}
                                  className="flex items-center justify-between rounded-[10px] border border-[#ebeff7] bg-[#f9fafc] px-3 py-1.5"
                                >
                                  <div>
                                    <p className="text-[11.5px] font-semibold text-[#3b4560]">
                                      {enr.billingLabel ?? enr.billingPeriod} ·{" "}
                                      {enr.quarter} {enr.academicYear}
                                    </p>
                                    {enr.coverageEndLabel && (
                                      <p className="text-[11px] text-[#8189a0]">
                                        Until {enr.coverageEndLabel}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <EnrolChip status={enr.status} />
                                    <span className="text-[11.5px] font-bold text-[#16213e]">
                                      {formatNaira(enr.feeAmount)}
                                    </span>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent payments */}
              {parent.recentPayments.length > 0 && (
                <div>
                  <p className="mb-3 text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                    Recent payments
                  </p>
                  <div className="overflow-hidden rounded-[16px] border border-[#ebeff7]">
                    {parent.recentPayments.map(
                      (
                        pmt: AdminParentDetail["recentPayments"][number],
                        i: number,
                      ) => (
                        <div
                          key={pmt.id}
                          className={`flex items-center justify-between px-4 py-3 ${i > 0 ? "border-t border-[#f7f8fb]" : ""}`}
                        >
                          <div>
                            <p className="text-xs font-semibold text-[#16213e]">
                              {pmt.description}
                            </p>
                            <p className="text-[11.5px] text-[#8189a0]">
                              {pmt.paidAt
                                ? formatDate(pmt.paidAt)
                                : formatDate(pmt.createdAt)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-bold text-[#16213e]">
                              {pmt.amountFormatted}
                            </p>
                            <span
                              className={`text-[11px] font-bold ${pmt.status === "paid" ? "text-[#2a7a56]" : "text-[#8189a0]"}`}
                            >
                              {pmt.status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */
export default function AdminParents() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useAdminGetParentsQuery({
    search: search || undefined,
    page,
    limit: 25,
  });
  const parents = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Parents"
        subtitle="View and manage parent accounts, their children, and payment history."
      />

      {/* Controls */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search by name or email…"
        />
        <p className="ml-auto text-xs text-[#8189a0]">
          {data?.total ?? 0} parents
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : parents.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No parents found"
          subtitle="Parents appear here once they register an account."
        />
      ) : (
        <TableContainer>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[#ebeff7]">
              <tr>
                <Th>Parent</Th>
                <Th>Email</Th>
                <Th>Children</Th>
                <Th>Tracks</Th>
                <Th>Active enrolments</Th>
                <Th>Joined</Th>
                <Th>{""}</Th>
              </tr>
            </thead>
            <tbody>
              {parents.map((p: AdminParent) => (
                <tr
                  key={p.id}
                  onClick={() => setSelected(p.id)}
                  className="cursor-pointer border-b border-[#f7f8fb] transition hover:bg-[#f9fafc]"
                >
                  <Td>
                    <div className="flex items-center gap-2.5">
                      <Avatar
                        firstName={p.firstName}
                        lastName={p.lastName}
                        size="sm"
                      />
                      <p className="font-semibold text-[#16213e]">
                        {p.firstName} {p.lastName}
                      </p>
                    </div>
                  </Td>
                  <Td>
                    <span className="text-xs text-[#5a6278]">{p.email}</span>
                  </Td>
                  <Td>
                    <span className="font-bold text-[#3b4560]">
                      {p.childCount}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {p.tracks.map((t: string) => (
                        <TrackChip key={t} track={t} />
                      ))}
                    </div>
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-bold
                      ${p.activeEnrolments > 0 ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#f1f3f8] text-[#8189a0]"}`}
                    >
                      {p.activeEnrolments} active
                    </span>
                  </Td>
                  <Td>
                    <span className="text-xs text-[#8189a0]">
                      {formatDate(p.createdAt)}
                    </span>
                  </Td>
                  <Td>
                    <svg
                      className="h-4 w-4 text-[#c9cfe0]"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M6 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={data?.page ?? 1}
            pages={data?.pages ?? 1}
            total={data?.total ?? 0}
            count={parents.length}
            onPage={(p) => {
              setPage(p);
              window.scrollTo({ top: 0 });
            }}
          />
        </TableContainer>
      )}

      {selected && (
        <ParentDrawer parentId={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}
